'use strict';

import BamLinker from 'bam-linker';
import BamArgValidator from 'bam-arg-validator';
const Helpers = BamLinker.getHelpers();

/**
 * Static functions which override functions defined in bam-linker/lib/DefaultFunctions.  In all cases,
 * these functions (and those in DefaultFunctions noted above) are passed as options to the MlbLinker
 * constructor.
 *
 * Consumers are not expected to invoke any of these functions directly, but they may be useful for reference.
 */
class FunctionOverrides {

    /**
     * Builds a aws player-page link.
     * @param {MlbLinker} linker The current MlbLinker instance
     * @param {object | null} statsapiPerson; required
     * @param fullname e.g. John Smith; optional
     * @param playerId Numeric player ID; optional
     * @return {string}
     */
    static buildPlayerPageLink(linker, statsapiPerson, fullname = '', id = '') {
        return linker.getLink('/player/' + linker._buildPlayerSlugFn(linker, statsapiPerson, fullname, id), false);
    }

    /**
     * @param {MlbLinker} linker The current MlbLinker instance
     * @param {object | null} statsapiPerson; required
     * @param fullname e.g. John Smith; optional
     * @param playerId Numeric player ID; optional
     * @return {string} e.g. "12345678" or "first-last-12345678"
     */
    static buildPlayerSlug(linker, statsapiPerson, fullname = '', id = '') {
        let slug = '';

        if (statsapiPerson && statsapiPerson.nameSlug) {
            slug = statsapiPerson.nameSlug;
        } else if (id !== '') {
            slug = id;
        }

        return slug;
    }

    /**
     * Returns a team-homepage URL.  Current team-homepage URLs are JSP for the desktop site, v2 for the mobile site.
     * Thus this function will return fully qualified URLs in one of these formats: http://m.yankees.mlb.com (v2), or
     * http://newyork.yankees.mlb.com/index.jsp?c_id=nyy (JSP).
     * @param {MlbLinker} linker The current MlbLinker instance
     * @param {string | number} teamId The numeric ID of the team, as a string or number
     * @return {string} A team-homepage link, in one of two formats depending on the client (see above).
     */
    static buildTeamHomepageLink(linker, teamId) {
        if (linker._isMilb) {
            const _teamId = teamId || linker._teamId;
            if (!_teamId || typeof(_teamId) !== 'number') {
                return null;
            }
            const slug = (linker._teamSlugs && linker._teamSlugs[_teamId]);

            if (linker._isMlbPartner) return null;
            return (slug) ? linker.getLink(`/${slug}`, false) : `${linker._milbHostName}/index.jsp?sid=t${_teamId}`;
        }

        if (linker._teamData.isTeamExists && !linker._teamData.isTeamExists(teamId)) {
            return null;
        }

        return linker.getLink(linker._teamData.getTeamSlug && linker._teamData.getTeamSlug(teamId), false);
    }

    /**
     * Override to build correct generic url in both mlb and milb context
     * @param {MlbLinker} linker The current MlbLinker instance
     * @param {string | number} urlSegment urlSegment The segment to append to the base URL.
     * e.g. "/player/some-player-121212". Segments which don't start with a forward-slash get
     * one prepended automatically.
     * @param {boolean} keepTeam If true and the current request's teamId is not zero (meaning, there's currently a
     * team segment on the URL), that segment will be preserved for the returned URL.
     * If false, it won't be. The default is true.
     * @return {string}
     */
    static buildLink(linker, urlSegment, keepTeam) {
        BamArgValidator.validateArgs(arguments, ['linker', 'urlSegment', 'keepTeam'],
            ['object', 'string', 'boolean'], [true, true, false]);
        let url;
        if (linker._isMilb) {
            url = (linker._baseUrl || '')
                + (linker._appView === 'web' ? '' : '/' + linker._appView)
                + (linker._langCode === 'en' ? '' : '/' + linker._langCode)
                + (keepTeam ? (linker._slug ? '/' + linker._slug : '') : '')
                + urlSegment;
        } else {
            url = linker._appViewPathEl
                + linker._langCodePathEl
                + (keepTeam ? linker._teamPathEl : '')
                + urlSegment;
        }

        return url;
    }

}

export default FunctionOverrides;
